import { FC, RefObject, useCallback, useContext, useEffect, useState } from 'react';
import { useWorkerCommentsStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useWorkerCommentsStore';
import { usePopper } from 'react-popper';
import { Range, useCurrentEditor } from '@tiptap/react';
import AddCommentBox from '../../../feedback/AddCommentBox';
import uuid from 'uuid';
import { TextSelection } from 'prosemirror-state';
import { ChatTaskContext } from '../../../contexts/ChatTaskContext';
import { debounce } from 'lodash';
import { getSelectionFromEditor } from '../../../components/editor/plugins/comment';

type TextSelectionCommentPopperProps = {
  enabled: boolean;
  anchorRef: RefObject<HTMLElement>;
  childId: string;
  turnIndex?: number;
  isLeftPosition?: boolean;
  // Inherited from TextSelectionCommentBox. Still necessary?
  overrideChildId?: string;
};

export const TextSelectionPopover: FC<TextSelectionCommentPopperProps> = ({
  enabled,
  anchorRef,
  childId,
  turnIndex,
  isLeftPosition = false,
  overrideChildId,
}) => {
  const taskContext = useContext(ChatTaskContext);
  const { workerId } = taskContext;

  const { editor } = useCurrentEditor();

  const deleteSelection = useWorkerCommentsStore(state => state.deleteSelection);
  const addWorkerComment = useWorkerCommentsStore(state => state.addWorkerComment);
  const selectWorkerComment = useWorkerCommentsStore(state => state.selectWorkerComment);

  const [commentText, setCommentText] = useState<string>('');

  const [popperRef, setPopperRef] = useState<HTMLDivElement | null>(null);

  const [selection, setSelection] = useState<Range | null>(null);
  const [content, setContent] = useState('');

  const isSelectionEmpty = (range: Range | null) => {
    if (!range) return true;
    if (range.from === range.to) return true;
    return false;
  };

  // Determine the start location of the popper based on the text selection
  const isSelectionPosValid =
    content !== '' &&
    selection &&
    editor &&
    selection.from >= 0 &&
    selection.from <= editor.view.state.doc.content.size;

  const start = isSelectionPosValid ? editor.view.coordsAtPos(selection.from) : undefined;

  const verticalOffset =
    start && anchorRef.current ? start.top - anchorRef.current.getBoundingClientRect().top : 0;

  const { styles, attributes } = usePopper(anchorRef.current, popperRef, {
    placement: isLeftPosition ? 'left-start' : 'right-start',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [verticalOffset, 28],
        },
      },
      {
        name: 'preventOverflow',
        options: {
          mainAxis: false,
        },
      },
    ],
  });

  useEffect(() => {
    if (!editor) {
      return;
    }
    const handleSelectionChange = debounce(({ editor }) => {
      const { range, content } = getSelectionFromEditor(editor);
      setSelection(range);
      setContent(content);
    }, 200);
    editor.on('selectionUpdate', handleSelectionChange);
    return () => {
      editor.off('selectionUpdate', handleSelectionChange);
    };
  }, [editor]);

  const discardComment = useCallback(() => {
    editor?.view?.dom.blur();
    editor?.view?.dispatch(
      editor?.view?.state.tr.setSelection(TextSelection.create(editor?.view?.state.doc, 0)),
    );
    deleteSelection();
  }, [deleteSelection, editor]);

  const submitComment = useCallback(
    (commentText: string) => {
      if (!selection) return;

      if (commentText.length) {
        const workerCommentId = uuid();
        addWorkerComment({
          workerCommentId,
          author: workerId,
          comment: commentText,
          turn: turnIndex,
          childId,
          overrideChildId,
          locationStart: selection.from,
          locationEnd: selection.to,
        });
        setCommentText('');
        selectWorkerComment(workerCommentId);
      }
      discardComment();
    },
    [
      selection,
      discardComment,
      addWorkerComment,
      workerId,
      turnIndex,
      childId,
      overrideChildId,
      selectWorkerComment,
    ],
  );

  // Return null if the editor view isn't available or there is no valid text selection
  if (
    !enabled ||
    !editor ||
    content === '' ||
    taskContext.enableUnifiedFeedback ||
    isSelectionEmpty(selection)
  ) {
    return null;
  }

  return (
    <div ref={setPopperRef} style={styles.popper} {...attributes.popper} className="z-30 flex">
      <AddCommentBox
        commentText={commentText}
        setCommentText={setCommentText}
        submitComment={submitComment}
        discardComment={discardComment}
        disableAutoFocus
      />
    </div>
  );
};
